import { useCallback, useEffect, useState } from 'react'; 
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { styled, Grid, Typography, Box } from '@mui/material';
import { Viewer } from "../../util/Constants";
import { useTitle } from '../../util/Common';
import { DataLakeDirectoryModel } from '../../gql-types.generated';
import { selectError, clearError, clearState, selectRootDirectories, selectActiveDirectory, selectIsDirectoryLoading, setDirectoryLoading } from './FileManagerSlice';
import { fetchFileDirectories, fetchDirectoryByPath } from './FileManagerActions';
import { viewerCanEdit } from '../../util/ViewerUtility';
import { DetailsTabContainer, DetailsTabHeader, DetailsTabHeaderWrapper, DetailsTabPanelContainer, CardAvatar, TabContainer, TabHeader } from '../../util/SharedStyles';
import TabPanel from '../../components/TabPanel';
import DetailsPage from '../../components/DetailsPage';
import DirectoryTree from '../../components/fileManagement/DirectoryTree';
import ActiveDirectoryExplorer from '../../components/fileManagement/ActiveDirectoryExplorer';
import FileSearch from '../../components/fileManagement/FileSearch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';


const TabContainerWithPadding = styled(TabContainer)(() => ({
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
}));

interface FileManagerProps {
    viewer: Viewer | undefined;
}

const FileManager: React.FC<FileManagerProps> = (props) => {
    const {viewer} = props;
    const dispatch = useAppDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [verticalAlign, setVerticalAlign] = useState<boolean>(false);
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
    
    // detailsPageProps
    const [detailsRecordLoaded, setDetailsRecordLoaded] = useState<boolean>(false);
    const [detailsPageTitle, setDetailsPageTitle] = useState<string | undefined>(undefined);
    const [detailsAV, setDetailsAV] = useState<JSX.Element | undefined>(undefined);
    const [detailsHeader, setDetailsHeader] = useState<string | undefined>(undefined);
    
    const error = useAppSelector(selectError);
    const rootDirectories = useAppSelector(selectRootDirectories);
    const activeDirectory = useAppSelector(selectActiveDirectory);
    const isDirectoryLoading = useAppSelector(selectIsDirectoryLoading);
    
    const canEdit = viewerCanEdit(viewer);

    const getFileManagerAV = useCallback(() => (
        <CardAvatar aria-label="file manager" alt="file manager">
            <FolderOpenIcon />
        </CardAvatar>
    ), []);

    // Initial fetch
    useEffect(() => {
        const fetchData = () => {
            setDetailsRecordLoaded(false); // Reset loading state
            dispatch(fetchFileDirectories());
        };
        fetchData();
        return () => {
            dispatch(clearState()); // Clear state on unmount
        };
    }, []);

    // Update UI based on data
    useEffect(() => {
        const hasResponse = rootDirectories !== undefined;
        if (hasResponse) {
            // Set up the UI whether we got directories or not
            setDetailsHeader("Root Directories");
            setDetailsRecordLoaded(true);
            setDetailsAV(getFileManagerAV());
            setDetailsPageTitle("File Manager");
        }
    }, [rootDirectories, getFileManagerAV]);

    useTitle(detailsPageTitle);
    

    // Handle errors
    useEffect(() => {
        if (error) {
            setDetailsRecordLoaded(true); // Stop the spinner
        }
        return () => {
            dispatch(clearError()); // Clear errors on unmount
        };
    }, [error]);
    
    const updateAlignment = (verticalAlign: boolean) => {
        setVerticalAlign(verticalAlign);
    };

    const handleTabChange = (error: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const onDirectoryClick = (directoryPath: string | undefined) => {
        if (directoryPath) {
            dispatch(setDirectoryLoading());
            dispatch(fetchDirectoryByPath(directoryPath));
        }
    };

    const getActiveDirectoryFileExplorer = () => {
        return (
            <TabContainerWithPadding>
                <Grid 
                    container 
                    sx={{ 
                        flexGrow: 1, 
                        overflow: 'hidden',
                        height: '100%'
                    }}
                >
                    <Grid item xs={12} sx={{ height: '100%' }}>
                        <ActiveDirectoryExplorer 
                            viewer={viewer}
                            directory={activeDirectory}
                            onDirectoryClick={handleDirectoryClick}
                            loading={isDirectoryLoading}
                            onRefreshTab={handleDirectoryClick}
                            onRefreshTree={refreshTree}
                        />
                    </Grid>
                </Grid>
            </TabContainerWithPadding>
        );
    };


    const expandToActiveDirectory = useCallback((activePath: string) => {
        const pathParts = activePath.split('/');
        let currentPath = '';
        const newPaths = new Set<string>();
        
        // Build up paths for each parent directory
        pathParts.forEach(part => {
            if (part) {
                currentPath = currentPath ? `${currentPath}/${part}` : part;
                newPaths.add(currentPath);
            }
        });
        
        setExpandedPaths(newPaths);
    }, []);

    // Auto-expand to active directory when it changes
    useEffect(() => {
        if (activeDirectory?.path) {
            expandToActiveDirectory(activeDirectory.path);
        }
    }, [activeDirectory?.path, expandToActiveDirectory]);

    const handleToggleExpand = useCallback((path: string) => {
        setExpandedPaths(prev => {
            const newPaths = new Set(prev);
            if (newPaths.has(path)) {
                newPaths.delete(path);
            } else {
                newPaths.add(path);
            }
            return newPaths;
        });
    }, []);

    const refreshTree = useCallback(() => {
        dispatch(fetchFileDirectories());
    }, []);

    const handleDirectoryClick = useCallback((path: string) => {
        setTabValue(0); // Switch to Active Directory tab
        onDirectoryClick(path);
    }, []);

    const getDirectoryTree = (): JSX.Element => {
        return (
            <DirectoryTree
                directoryHierarchy={rootDirectories}
                expandedPaths={expandedPaths}
                activeDirectory={activeDirectory}
                onDirectoryClick={handleDirectoryClick}
                onToggleExpand={handleToggleExpand}
            />
        );
    }


    const getTabbedContainer = () => {
        return (
            <DetailsTabContainer>
                <DetailsTabHeaderWrapper>
                    <DetailsTabHeader
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="scrollable error tabs"
                    >
                        <TabHeader label="Active Directory" id='vertical-tab-file-manager-active-directory' />
                        <TabHeader label="File Search" id='vertical-tab-file-manager-search' />
                    </DetailsTabHeader>
                </DetailsTabHeaderWrapper>
                <DetailsTabPanelContainer>
                    <TabPanel verticalAlign={verticalAlign} value={tabValue} index={0}>
                        {getActiveDirectoryFileExplorer()}
                    </TabPanel>
                    <TabPanel verticalAlign={verticalAlign} value={tabValue} index={1}>
                        <FileSearch 
                            viewer={viewer}
                            onDirectoryClick={handleDirectoryClick}
                            onTabChange={handleTabChange}
                        />
                    </TabPanel>
                </DetailsTabPanelContainer>
            </DetailsTabContainer>
        );
    }

    return (
        <DetailsPage
            recordLoaded={detailsRecordLoaded}
            pageTitle={detailsPageTitle}
            detailsAV={detailsAV}
            detailsHeader={detailsHeader}
            detailsSubHeader={undefined}
            detailsChips={undefined}
            getDetailsListContainer={getDirectoryTree}
            getTabbedContainer={getTabbedContainer}
            error={error}
            updateAlignment={updateAlignment}
            toolBarButtons={undefined}
            detailsEditButton={undefined}
            detailsFavoriteButton={undefined}
            getDialogs={() => undefined}
        />
    );
}

export default FileManager;
